// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2025-2026 Daragh Farrell

/**
 * AuthProvider - Handles token refresh on app initialization
 *
 * This provider attempts to refresh the access token when the app loads.
 * If the user has a valid refresh token (httpOnly cookie), they'll get
 * a new access token and can continue their session. Otherwise, they'll
 * need to log in.
 */

/* eslint-disable react-refresh/only-export-components -- Context modules export both Provider and hook by design */
import { createContext, useContext, useEffect, useState, ReactNode, useMemo, useCallback, useRef } from 'react';
import { refreshAccessToken, hasAccessToken, clearAccessToken } from './auth-store';
import { useSession } from '../context/SessionContext';

type AuthContextValue = {
  isAuthenticating: boolean;
  isAuthenticated: boolean;
  triggerRefresh: () => Promise<boolean>;
  setAuthenticated: (value: boolean) => void;
};

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const { session, clearSession } = useSession();
  const [isAuthenticating, setIsAuthenticating] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  // Use refs for values that shouldn't trigger re-runs
  const sessionRef = useRef(session);
  const clearSessionRef = useRef(clearSession);
  sessionRef.current = session;
  clearSessionRef.current = clearSession;

  // Attempt to refresh token on mount if we have a session but no token
  useEffect(() => {
    const initAuth = async () => {
      // If we already have a valid token, we're authenticated
      if (hasAccessToken()) {
        setIsAuthenticated(true);
        setIsAuthenticating(false);
        return;
      }

      // If we have a session (user was previously logged in), try to refresh
      if (sessionRef.current) {
        try {
          const success = await refreshAccessToken();
          setIsAuthenticated(success);
          if (!success) {
            // Refresh failed - session is invalid, clear it
            clearSessionRef.current();
          }
        } catch {
          // Refresh failed
          clearSessionRef.current();
          setIsAuthenticated(false);
        }
      } else {
        // No session, not authenticated
        setIsAuthenticated(false);
      }

      setIsAuthenticating(false);
    };

    initAuth();
  }, []); // Only run on mount - refs provide access to current values

  // Manual refresh trigger for components that need it
  const triggerRefresh = useCallback(async (): Promise<boolean> => {
    try {
      const success = await refreshAccessToken();
      setIsAuthenticated(success);
      if (!success) {
        clearAccessToken();
      }
      return success;
    } catch {
      clearAccessToken();
      setIsAuthenticated(false);
      return false;
    }
  }, []);

  // Allow Login page to signal successful authentication
  const setAuthenticated = useCallback((value: boolean) => {
    setIsAuthenticated(value);
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({
      isAuthenticating,
      isAuthenticated,
      triggerRefresh,
      setAuthenticated,
    }),
    [isAuthenticating, isAuthenticated, triggerRefresh, setAuthenticated]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return ctx;
}
